import { useEffect, useState } from 'react';
import './login.scss';
import { useNavigate } from 'react-router-dom';
import { forgetPassword, postLogin } from "../../common/api/apiCalls";
import { useUser } from "../../common/contexts/UserContext";
import { roles } from "../../common/types/roles";
import { svgs } from "../../common/assats";
import { useTranslation } from "react-i18next";
import TextInputWithIcon from "../../components/TextInputWithIcon";
import PasswordInputWithIcon from "../../components/PasswordInputWithIcon";
import Button from "../../components/Button";
import { loginSchema } from "../../common/consts/yupSchems";
import { ValidationError } from "yup";
import { useAlert } from "../../common/contexts/AlertContext";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

function Login() {
  const { t } = useTranslation();
  const { user, fetchUser } = useUser();
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [errors, setErrors] = useState<{
    email?: string[];
    password?: string[];
  }>({});
  const alert = useAlert();
  const getText = (text: string) => t(`login.${text}`);
  const { executeRecaptcha } = useGoogleReCaptcha();

  async function validate() {
    try {
      await loginSchema.validate({ password, email }, { abortEarly: false });
      setErrors({});
      return true; // Validation succeeded
    } catch (err: any) {
      if (err instanceof ValidationError) {
        const validationErrors: { email?: string[]; password?: string[] } = {
          email: [],
          password: [],
        };
        err.inner.forEach((error: any) => {
          const path = error.path as keyof typeof validationErrors;
          if (path && validationErrors[path]) {
            validationErrors[path]?.push(error.message); // Collect multiple errors for each field
          }
        });
        setErrors(validationErrors);
      }
      return false; // Validation failed
    }
  }
  async function submit() {
    if (await validate()) {
      if (!executeRecaptcha) {
        console.error("reCAPTCHA function not available");
        return;
      }
      let captchaValue: string | null = null;
      try {
        captchaValue = await executeRecaptcha("login");
      } catch (err) {
        console.error("Error executing reCAPTCHA:", err);
      }
      if (!captchaValue) {
        alert("Error with reCAPTCHA, please try again.", "error");
        return;
      }
      const res = await postLogin({
        username: email,
        password: password,
        recaptchaToken: captchaValue,
      });
      if (res.success) {
        if (res.data?.needOtp) {
          if (res.data.role === roles.Admin) {
            const res = await forgetPassword(email);
            if (res?.success) {
              navigate("/ResetPasswordOTP", {
                state: { email, otpType: "adminOtp" },
              });
            } else {
              alert("Cannot send email", "error");
            }
          } else if (res.data.role === roles.HCP) {
            const res = await forgetPassword(email);
            if (res?.success) {
              navigate("/ResetPasswordOTP", {
                state: { email, otpType: "resetPassword" },
              });
            } else {
              alert("Cannot send email", "error");
            }
          }

          return;
        }
        await fetchUser();
        navigate("/Home/Dashboard", { replace: true });
        return;
      }
      alert((res?.err as string) || getText("loginFailed"), "error");
    }
  }
return (
  <div
    onKeyDown={(e) => {
      if (e.key === "Enter") submit();
    }}
    className="login-main-container flex-center"
  >
    <div className="login-content">
      <h3 className="headline"> {getText("welcome_to")}</h3>
      <svgs.logos.Logo className="logo" />
      <div className="input-wrapper">
        <p className="text">{getText("Login_below_to_get_started")}</p>
        <TextInputWithIcon
          errors={errors}
          label={getText("Email")}
          Icon={svgs.logos.Mail}
          placeholder={getText("Enter_your_email")}
          setValue={setEmail}
          value={email}
          inputName="email"
          filedCorrect={
            Boolean(errors?.email && !email.length) ? false : undefined
          }
        />
        <PasswordInputWithIcon
          label={getText("Password")}
          inputName="password"
          errors={errors}
          placeholder={getText("Enter_your_password")}
          setValue={setPassword}
          value={password}
        />
      </div>
      <Button onClick={submit} text={getText("Login")} />
    </div>
  </div>
);
}

export default Login;
